import React, { useState } from 'react';
import { Shield, Lock, CheckCircle, X } from 'lucide-react';

/**
 * Selo de Segurança - Componente que exibe certificação de segurança do site
 * 
 * Características:
 * - Selo dinâmico com informações de certificação
 * - Modal com detalhes ao clicar
 * - Indica SSL/HTTPS ativo
 * - Aumenta confiança do usuário
 */
const SecuritySeal = ({ position = 'bottom-right', showModal = true }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const currentDate = new Date();
  const validUntil = new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), currentDate.getDate());
  
  const securityInfo = {
    siteName: 'Minhas Composições',
    domain: 'minhascomposicoes.com.br',
    sslProvider: 'Let\'s Encrypt',
    encryptionType: 'TLS 1.3 / AES-256',
    validFrom: currentDate.toLocaleDateString('pt-BR'),
    validUntil: validUntil.toLocaleDateString('pt-BR'),
    verifiedBy: 'Emergent Security',
    lastScan: currentDate.toLocaleDateString('pt-BR'),
    threats: 0,
    vulnerabilities: 0
  };

  const handleClick = () => {
    if (showModal) {
      setIsModalOpen(true);
    }
  };

  // Posições do selo
  const positionClasses = {
    'bottom-right': 'fixed bottom-4 right-4 z-50',
    'bottom-left': 'fixed bottom-4 left-4 z-50',
    'inline': 'inline-flex'
  };

  return (
    <>
      {/* Selo de Segurança */}
      <div 
        className={`${positionClasses[position]} cursor-pointer group`}
        onClick={handleClick}
        title="Clique para ver detalhes de segurança"
      >
        <div className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 
                        text-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 
                        transition-all duration-300 transform hover:scale-105 border border-green-400">
          <div className="relative">
            <Shield className="w-6 h-6 text-white" />
            <CheckCircle className="w-3 h-3 text-green-300 absolute -bottom-1 -right-1 bg-green-700 rounded-full" />
          </div>
          <div className="flex flex-col">
            <span className="text-xs font-bold tracking-wide">SITE SEGURO</span>
            <span className="text-[10px] opacity-90 flex items-center gap-1">
              <Lock className="w-3 h-3" />
              SSL/TLS Ativo
            </span>
          </div>
        </div>
        
        {/* Tooltip */}
        <div className="absolute bottom-full right-0 mb-2 w-48 bg-gray-900 text-white text-xs p-2 rounded 
                        opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          Clique para ver os detalhes de segurança do site
        </div>
      </div>

      {/* Modal de Detalhes de Segurança */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
             onClick={() => setIsModalOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
               onClick={(e) => e.stopPropagation()}>
            
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-3 rounded-full">
                    <Shield className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Certificado de Segurança</h2>
                    <p className="text-green-100 text-sm">Site Verificado e Protegido</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Status de Segurança */}
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl border border-green-200">
                <CheckCircle className="w-10 h-10 text-green-600" />
                <div>
                  <p className="font-bold text-green-800">✅ Site 100% Seguro</p>
                  <p className="text-sm text-green-600">Nenhuma ameaça detectada</p>
                </div>
              </div>

              {/* Informações do Certificado */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-green-600" />
                  Informações do Certificado SSL
                </h3>
                
                <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Domínio:</span>
                    <span className="font-medium text-gray-800">{securityInfo.domain}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Provedor SSL:</span>
                    <span className="font-medium text-gray-800">{securityInfo.sslProvider}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Criptografia:</span>
                    <span className="font-medium text-gray-800">{securityInfo.encryptionType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Válido até:</span>
                    <span className="font-medium text-green-600">{securityInfo.validUntil}</span>
                  </div>
                </div>
              </div>

              {/* Verificação de Segurança */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-600" />
                  Última Verificação de Segurança
                </h3>
                
                <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Data da verificação:</span>
                    <span className="font-medium text-gray-800">{securityInfo.lastScan}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Ameaças encontradas:</span>
                    <span className="font-medium text-green-600">{securityInfo.threats} (Nenhuma)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Vulnerabilidades:</span>
                    <span className="font-medium text-green-600">{securityInfo.vulnerabilities} (Nenhuma)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Verificado por:</span>
                    <span className="font-medium text-gray-800">{securityInfo.verifiedBy}</span>
                  </div>
                </div>
              </div>

              {/* Google Safe Browsing */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Google Safe Browsing
                </h3>
                
                <a 
                  href="https://transparencyreport.google.com/safe-browsing/search?url=minhascomposicoes.com.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-xl border border-gray-200 transition-colors"
                >
                  <div className="bg-white p-2 rounded-lg shadow-sm">
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">Site verificado pelo Google</p>
                    <p className="text-sm text-gray-500">Clique para ver o relatório de segurança</p>
                  </div>
                  <CheckCircle className="w-6 h-6 text-green-500" />
                </a>
              </div>

              {/* Garantias */}
              <div className="bg-blue-50 rounded-xl p-4 space-y-2">
                <h4 className="font-semibold text-blue-800 text-sm">🔒 Suas Garantias:</h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>✓ Dados criptografados em trânsito (HTTPS)</li>
                  <li>✓ Proteção contra interceptação de dados</li>
                  <li>✓ Certificado digital válido e verificado</li>
                  <li>✓ Conformidade com LGPD (Lei Geral de Proteção de Dados)</li>
                  <li>✓ Pagamentos processados em ambiente seguro</li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t">
              <p className="text-xs text-gray-500 text-center">
                Este selo garante que o site <strong>{securityInfo.siteName}</strong> utiliza 
                criptografia de ponta para proteger seus dados pessoais e financeiros.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Selo de Segurança para Footer - Versão compacta
 */
export const SecuritySealFooter = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  return (
    <>
      <div 
        className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
        onClick={() => setIsModalOpen(true)}
      >
        <div className="bg-green-600 p-1.5 rounded">
          <Shield className="w-4 h-4 text-white" />
        </div>
        <div className="text-left">
          <p className="text-xs font-semibold text-gray-800">Site Seguro</p>
          <p className="text-[10px] text-gray-500 flex items-center gap-1">
            <Lock className="w-3 h-3" /> SSL/HTTPS
          </p>
        </div>
      </div>
      
      {/* Reutiliza o mesmo modal */}
      {isModalOpen && (
        <SecuritySeal position="inline" />
      )}
    </>
  );
};

/**
 * Badge de Segurança para páginas de pagamento
 */
export const PaymentSecurityBadge = () => {
  return (
    <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
      <div className="bg-green-600 p-2 rounded-full">
        <Lock className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-sm font-semibold text-green-800">Pagamento 100% Seguro</p>
        <p className="text-xs text-green-600">Seus dados estão protegidos por criptografia SSL/TLS</p>
      </div>
      <Shield className="w-8 h-8 text-green-600 ml-auto" />
    </div>
  );
};

export default SecuritySeal;
